"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetlistValidationRemediation = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const netlist_gate_1 = require("../Types/netlist-gate");
const remediation_1 = require("../Types/remediation");
class NetlistValidationRemediation {
    static { this.MIN_UNKNOWN_DESIGNATORS = 10; }
    static { this.DECISIVE_SHARE = 0.8; }
    static forResult(result, netlistSource) {
        const rejectedRows = result.summary.oracleRejectedRows?.lineNumbers.length ?? 0;
        if (rejectedRows > 0) {
            return {
                owner: remediation_1.RemediationOwner.Dev,
                summary: `Our netlist reader could not represent ${rejectedRows} ${rejectedRows === 1 ? 'row' : 'rows'} of this netlist, so the comparison is incomplete.`,
                steps: [
                    'Escalate to dev with the build id: this is a limitation in our netlist reader, not a fault in the netlist.',
                ],
            };
        }
        const hasErrors = result.errors.some((error) => error.severity === undefined || error.severity === sigma_engine_shared_1.BoundaryLevel.Error);
        if (hasErrors && netlistSource === netlist_gate_1.NetlistSource.Generated) {
            return {
                owner: remediation_1.RemediationOwner.Dev,
                summary: "The project model disagrees with a netlist the CAD tool exported itself, which most likely points to a bug in Cadstrom's parser.",
                steps: [
                    'Escalate to dev with the build id and the findings below.',
                    'Accept the errors only if dev confirms the analysis can proceed on this model.',
                ],
            };
        }
        const unknownDesignators = result.summary.unknownDesignators;
        if (unknownDesignators !== undefined &&
            unknownDesignators.count >= NetlistValidationRemediation.MIN_UNKNOWN_DESIGNATORS) {
            const { count, matchedInOtherForm } = unknownDesignators;
            const unmatched = count - matchedInOtherForm;
            if (matchedInOtherForm >= count * NetlistValidationRemediation.DECISIVE_SHARE) {
                return {
                    owner: remediation_1.RemediationOwner.Dev,
                    summary: `${matchedInOtherForm} of the ${count} designators the project model lacks are in it under another form, so the netlist matches the design and the mismatch most likely points to a bug in Cadstrom's parser.`,
                    steps: [
                        'Do not re-upload the netlist: a replacement will not change the result.',
                        'Escalate to dev with the build id and the findings below.',
                        'Accept the errors only if dev confirms the analysis can proceed on this model.',
                    ],
                };
            }
            if (netlistSource === netlist_gate_1.NetlistSource.Uploaded &&
                unmatched >= count * NetlistValidationRemediation.DECISIVE_SHARE) {
                return {
                    owner: remediation_1.RemediationOwner.Ops,
                    summary: `The netlist names ${unmatched} parts the project model does not have in any form, so it is most likely from another design or board, or the upload is missing schematic sheets.`,
                    steps: [
                        "Check that the netlist was exported from this board's project.",
                        'Check that the uploaded CAD files include every schematic sheet of the design.',
                        'Upload the right netlist from the netlist column, or re-upload the complete design.',
                        'If both are right, escalate to dev as a possible parser bug.',
                    ],
                };
            }
        }
        if (!hasErrors) {
            return {
                owner: remediation_1.RemediationOwner.Ops,
                summary: 'Validation found only warnings against the netlist.',
                steps: [
                    'Review the findings below.',
                    'If they are cosmetic (for example, net names that differ only in form), accept them with a reason.',
                    'Otherwise, escalate to dev with the build id.',
                ],
            };
        }
        if (netlistSource === undefined) {
            return {
                owner: remediation_1.RemediationOwner.Ops,
                summary: 'The project model disagrees with the netlist, and where that netlist came from was not recorded.',
                steps: [
                    'Check the build files for which netlist was validated: an upload, or one the generator produced.',
                    'If it was uploaded and is stale or from another design, upload a replacement from the netlist column.',
                    'If it was generated, or matches the CAD files, escalate to dev as a possible parser bug.',
                ],
            };
        }
        return {
            owner: remediation_1.RemediationOwner.Ops,
            summary: 'The project model disagrees with the netlist that was supplied for this build.',
            steps: [
                'Check that the netlist was exported from the same revision of the design as the uploaded CAD files.',
                'If it is stale or from another design, upload a replacement from the netlist column.',
                'If it matches the CAD files, escalate to dev as a possible parser bug.',
            ],
        };
    }
}
exports.NetlistValidationRemediation = NetlistValidationRemediation;
//# sourceMappingURL=remediation.js.map